import React, { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, AlertCircle, Download, Clock } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * LiveActionStatus
 * Polla GET /api/requests/{id} ogni POLL_INTERVAL_MS e mostra lo stato live
 * di una visura camerale (o altro servizio async) lanciata dall'assistente AI.
 *
 * Stati supportati:
 *  - "pending"   → spinner "in elaborazione"
 *  - "completed" → bottone "Scarica PDF"
 *  - "failed"    → messaggio errore
 *  - "timeout"   → "Ancora in elaborazione, controlla lo storico"
 *
 * Al primo render forza un POST /api/requests/{id}/poll per non aspettare
 * il cron job (ogni 30s) e ottenere risposta in ~10–30 secondi.
 */
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 80; // ~6.5 minuti (80 * 5s)

const STATUS_LABEL = {
  visura_ord_societa_capitali: "Visura camerale (società di capitali)",
  visura_ord_societa_persone: "Visura camerale (società di persone)",
  visura_ord_impresa_individuale: "Visura camerale (impresa individuale)",
};

export const LiveActionStatus = ({ meta }) => {
  const reqId = meta?.executed_request_id;
  const serviceId = meta?.executed_service_id;
  const [status, setStatus] = useState("pending");
  const [pdfFilename, setPdfFilename] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!reqId) return undefined;
    let cancelled = false;
    let attempts = 0;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/requests/${reqId}`);
        if (cancelled) return "cancelled";
        const s = data?.status || "pending";
        if (s === "completed") {
          setStatus("completed");
          setPdfFilename(
            data?.pdf_filename ||
            `visura_${(data?.params?.cf_piva_id || "azienda")}.pdf`
          );
          return "done";
        }
        if (s === "failed") {
          setStatus("failed");
          setErrorMsg(data?.error_message || "Errore non specificato");
          return "done";
        }
        return "pending";
      } catch (e) {
        console.warn("[LiveActionStatus] poll error:", e?.message);
        return "pending";
      }
    };

    const loop = () => {
      if (cancelled) return;
      if (attempts >= MAX_POLL_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      const tid = setTimeout(async () => {
        attempts += 1;
        const result = await fetchOnce();
        if (result === "pending") loop();
      }, POLL_INTERVAL_MS);
      timerRef.current = tid;
    };

    (async () => {
      try {
        await api.post(`/requests/${reqId}/poll`);
      } catch {
        // ok se fallisce, il cron del backend prende comunque il lavoro
      }
      if (cancelled) return;
      const initial = await fetchOnce();
      if (initial === "pending") loop();
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [reqId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/requests/${reqId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfFilename || `visura_${reqId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Visura scaricata");
    } catch (e) {
      toast.error(`Errore download: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (!reqId) return null;

  const label = STATUS_LABEL[serviceId] || "Visura camerale";

  if (status === "completed") {
    return (
      <div
        className="mt-3 flex items-center justify-between gap-3 p-3 rounded-md bg-success-bg/40 border border-success/30"
        data-testid={`live-action-${reqId}`}
        data-status="completed"
      >
        <div className="flex items-center gap-2 text-sm text-success-strong font-medium">
          <CheckCircle2 size={16} strokeWidth={2.2} />
          <span>{label} pronta</span>
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={downloading}
          data-testid={`download-pdf-${reqId}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-success-strong text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 transition"
        >
          {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          {downloading ? "Scaricamento…" : "Scarica PDF"}
        </button>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div
        className="mt-3 flex items-start gap-2 p-3 rounded-md bg-danger-bg/40 border border-danger/30 text-sm text-danger-strong"
        data-testid={`live-action-${reqId}`}
        data-status="failed"
      >
        <AlertCircle size={16} strokeWidth={2.2} className="flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium">Visura non disponibile.</div>
          <div className="text-xs text-danger-strong/85 mt-0.5">{errorMsg}</div>
          <div className="text-xs text-text-muted mt-1">
            Il costo non viene addebitato in caso di errore.
          </div>
        </div>
      </div>
    );
  }

  if (status === "timeout") {
    return (
      <div
        className="mt-3 flex items-center gap-2 p-3 rounded-md bg-subtle border border-border text-sm text-text-secondary"
        data-testid={`live-action-${reqId}`}
        data-status="timeout"
      >
        <Clock size={16} strokeWidth={2.2} />
        <span>
          Visura ancora in elaborazione. Controlla nello{" "}
          <a href="/history" className="text-accent underline">storico richieste</a> tra qualche minuto.
        </span>
      </div>
    );
  }

  // pending
  return (
    <div
      className="mt-3 flex items-center gap-2 p-3 rounded-md bg-accent-subtle/40 border border-accent/30 text-sm text-text-secondary"
      data-testid={`live-action-${reqId}`}
      data-status="pending"
    >
      <Loader2 size={16} className="animate-spin text-accent" />
      <span>
        <span className="font-medium text-text-primary">{label}</span> in elaborazione…
        <span className="text-xs text-text-muted ml-2">
          (servizio CCIAA ufficiale, ~90 secondi)
        </span>
      </span>
    </div>
  );
};

export default LiveActionStatus;
